import { useRef } from 'react';
import { Avatar, Box, IconButton, Tooltip } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete'; // Import Delete Icon

/**
 * Circular avatar with a camera-overlay tap target for image selection.
 * Shows photo if src is set, falls back to initials.
 *
 * Props:
 * src        — current photo URL (null = use initials)
 * initials   — text shown when no photo is available
 * size       — diameter in px (default 80)
 * onChange   — called with the selected File
 * onRemove   — called when the delete icon is clicked
 * disabled   — suppress the picker
 */
export function AvatarPicker({ src, initials, size = 80, onChange, onRemove, disabled = false }) {
  const inputRef = useRef(null);

  const handleClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onChange(file);
    e.target.value = '';
  };

  const handleRemove = (e) => {
    e.stopPropagation(); // Prevent triggering the file picker
    if (onRemove) onRemove();
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          cursor: disabled ? 'default' : 'pointer',
          position: 'relative',
        }}
        onClick={handleClick}
      >
        <Avatar
          src={src || undefined}
          sx={{ width: size, height: size, fontSize: size * 0.35 }}
          alt={initials}
        >
          {!src && initials}
        </Avatar>

        {!disabled && (
          <Tooltip title="Change photo">
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 1 },
              }}
            >
              <IconButton size="small" sx={{ color: 'white', p: 0 }}>
                <CameraAltIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Delete Button */}
      {src && !disabled && (
        <Tooltip title="Remove photo" placement="right">
          <IconButton
            onClick={handleRemove}
            size="small"
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              backgroundColor: 'white',
              color: 'error.main',
              boxShadow: 2,
              '&:hover': { backgroundColor: '#fff5f5' },
              zIndex: 2,
            }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
}